import React, { useState } from 'react';
import { Announcement, Poll, GroupMemberProfile } from '../types';
import { Megaphone, Vote, Plus, CheckCircle2, Pin } from 'lucide-react';

interface CommunityViewProps {
  announcements: Announcement[];
  polls: Poll[];
  membersMap: Map<string, GroupMemberProfile>;
  activeMember: GroupMemberProfile;
  onVotePoll: (pollId: string, optionId: string) => void;
  onCreatePoll: (question: string, options: string[]) => void;
  onCreateAnnouncement: (title: string, content: string) => void;
}

export const CommunityView: React.FC<CommunityViewProps> = ({
  announcements,
  polls,
  membersMap,
  activeMember,
  onVotePoll,
  onCreatePoll,
  onCreateAnnouncement
}) => {
  const [showPollModal, setShowPollModal] = useState(false);
  const [showAnnModal, setShowAnnModal] = useState(false);

  const [question, setQuestion] = useState('');
  const [opt1, setOpt1] = useState('');
  const [opt2, setOpt2] = useState('');

  const [annTitle, setAnnTitle] = useState('');
  const [annContent, setAnnContent] = useState('');

  const handleCreatePollSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!question || !opt1 || !opt2) return;
    onCreatePoll(question, [opt1, opt2]);
    setShowPollModal(false);
    setQuestion('');
    setOpt1('');
    setOpt2('');
  };

  const handleCreateAnnSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!annTitle || !annContent) return;
    onCreateAnnouncement(annTitle, annContent);
    setShowAnnModal(false);
    setAnnTitle('');
    setAnnContent('');
  };

  const isOrganizer = ['owner', 'admin', 'organizer'].includes(activeMember.role);

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-20">
      
      {/* Top Banner */}
      <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-white">Community & Polls</h2>
          <p className="text-xs text-slate-400">Official club announcements, match time voting, and member polls</p>
        </div>

        <div className="flex gap-2">
          {isOrganizer && (
            <button
              onClick={() => setShowAnnModal(true)}
              className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-amber-300 font-bold text-xs rounded-xl border border-slate-700 transition"
            >
              + Announcement
            </button>
          )}
          <button
            onClick={() => setShowPollModal(true)}
            className="px-3.5 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs rounded-xl transition"
          >
            + Create Poll
          </button>
        </div>
      </div>

      {/* Announcements */}
      <div className="space-y-3">
        <h3 className="text-xs font-extrabold uppercase text-slate-400 tracking-wider flex items-center gap-1.5">
          <Megaphone className="w-4 h-4 text-amber-400" /> Pinned Group Announcements
        </h3>

        {announcements.map((a) => (
          <div key={a.id} className="bg-slate-900 border border-amber-500/30 p-5 rounded-3xl space-y-2 relative">
            <div className="flex justify-between items-center">
              <span className="font-extrabold text-base text-amber-300 flex items-center gap-1.5">
                <Pin className="w-4 h-4 fill-amber-300" /> {a.title}
              </span>
              <span className="text-[10px] text-slate-500 font-mono">{a.timestamp}</span>
            </div>
            <p className="text-xs text-slate-200 leading-relaxed">{a.content}</p>
            <div className="text-[10px] text-slate-400 font-semibold pt-2 border-t border-slate-800/80">
              Posted by {a.authorName} ({a.authorRole})
            </div>
          </div>
        ))}
      </div>

      {/* Polls */}
      <div className="space-y-3 pt-4">
        <h3 className="text-xs font-extrabold uppercase text-slate-400 tracking-wider flex items-center gap-1.5">
          <Vote className="w-4 h-4 text-emerald-400" /> Interactive Group Polls
        </h3>

        {polls.map((p) => {
          const totalVotes = p.options.reduce((sum, o) => sum + o.voterMemberIds.length, 0);

          return (
            <div key={p.id} className="bg-slate-900 border border-slate-800 p-5 rounded-3xl space-y-3">
              <h4 className="font-bold text-sm text-white">{p.question}</h4>

              <div className="space-y-2">
                {p.options.map((opt) => {
                  const hasVoted = opt.voterMemberIds.includes(activeMember.id);
                  const pct = totalVotes > 0 ? Math.round((opt.voterMemberIds.length / totalVotes) * 100) : 0;

                  return (
                    <button
                      key={opt.id}
                      onClick={() => onVotePoll(p.id, opt.id)}
                      className={`w-full p-3 rounded-2xl border text-left transition relative overflow-hidden flex justify-between items-center ${
                        hasVoted ? 'border-emerald-400 bg-emerald-950/30' : 'border-slate-800 bg-slate-950 hover:border-slate-700'
                      }`}
                    >
                      <div className="relative z-10 font-medium text-xs text-slate-200 flex items-center gap-2">
                        {hasVoted && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                        <span>{opt.text}</span>
                      </div>
                      <span className="relative z-10 text-xs font-mono font-bold text-emerald-400">
                        {pct}% ({opt.voterMemberIds.length})
                      </span>

                      {/* Bar Fill */}
                      <div
                        className="absolute left-0 top-0 bottom-0 bg-emerald-500/15 pointer-events-none transition-all duration-300"
                        style={{ width: `${pct}%` }}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal Create Poll */}
      {showPollModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl p-5 space-y-4">
            <h3 className="font-extrabold text-lg text-white">Create Group Poll</h3>
            <form onSubmit={handleCreatePollSubmit} className="space-y-3 text-xs">
              <input
                type="text"
                required
                placeholder="Question (e.g. Which time works best?)"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 text-white p-2.5 rounded-xl"
              />
              <input
                type="text"
                required
                placeholder="Option 1"
                value={opt1}
                onChange={(e) => setOpt1(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 text-white p-2.5 rounded-xl"
              />
              <input
                type="text"
                required
                placeholder="Option 2"
                value={opt2}
                onChange={(e) => setOpt2(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 text-white p-2.5 rounded-xl"
              />
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowPollModal(false)} className="flex-1 py-2 bg-slate-800 text-slate-300 font-bold rounded-xl">Cancel</button>
                <button type="submit" className="flex-1 py-2 bg-emerald-500 text-slate-950 font-black rounded-xl">Publish Poll</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Create Announcement */}
      {showAnnModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl p-5 space-y-4">
            <h3 className="font-extrabold text-lg text-white">Create Group Announcement</h3>
            <form onSubmit={handleCreateAnnSubmit} className="space-y-3 text-xs">
              <input
                type="text"
                required
                placeholder="Announcement Title"
                value={annTitle}
                onChange={(e) => setAnnTitle(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 text-white p-2.5 rounded-xl"
              />
              <textarea
                rows={3}
                required
                placeholder="Content..."
                value={annContent}
                onChange={(e) => setAnnContent(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 text-white p-2.5 rounded-xl"
              />
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowAnnModal(false)} className="flex-1 py-2 bg-slate-800 text-slate-300 font-bold rounded-xl">Cancel</button>
                <button type="submit" className="flex-1 py-2 bg-amber-500 text-slate-950 font-black rounded-xl">Post Announcement</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
